import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import FlyoutModal from '../../../components/common/FlyoutModal';
import BuyMoreSaveMore from '../BuyMoreSaveMore';
import FreeGiftAOV from '../FreeGiftAOV';
import messages from './messages';
import BundleGiftAOV from '../BundleGiftAOV';
import FlatPercentOff from '../FlatPercentOff';
import ProductSelectedHeader from '../../../components/common/ProductSelectedHeader';
import { FLYOUT_MODAL_THEME_AOV } from '../../../components/common/FlyoutModal/constants';
import PromoBannerTracker from '../../PromoBannerTracker';

import {
  PURPLE_CLOUD_PILLOW,
  SIZE_ATTRIBUTE_NAME,
} from '../../../utils/common/constants';
import useDrupalSettings from '../../../hooks/useDrupalSettings';
import {
  findVariantBySku,
  findVariantAttributeByName,
} from '../../../utils/variants';

const AovModal = ({ onCloseModal, aovs }) => {
  const { promotionalBanner } = useDrupalSettings();
  const { formatMessage, locale } = useIntl();
  const { product } = useDrupalSettings();

  const {
    selectedSKU,
    selectedSecondSKU,
    selectedBedFrameAOVSKUs,
    selectedBundlePdpAOVSKUs,
  } = useSelector((state) => state.pdp);
  const selectedSkuAov = aovs?.find((aov) => aov.targetSku === selectedSKU);
  const buyMoreAov = selectedSkuAov?.buyMore;
  const giftsAov = selectedSkuAov?.gifts;
  const flatRateAov = selectedSkuAov?.flatRate;
  const bundleGiftsAov = selectedSkuAov?.bundleGifts;
  const addedSkus = [
    selectedSKU,
    selectedSecondSKU,
    ...selectedBedFrameAOVSKUs,
    ...selectedBundlePdpAOVSKUs,
  ].filter(Boolean);

  const { promoBanner } = useSelector((state) => state.cart);

  const { formattedItems } = useSelector((state) => {
    const { lineItems } = state.cart;

    const items = lineItems
      .filter(({ variant }) => addedSkus.includes(variant.sku))
      .filter(
        (item, index, self) =>
          self.findIndex((v) => v.variant.sku === item.variant.sku) === index,
      );

    return {
      formattedItems: items.map(({ id, name, variant }) => ({
        id,
        name,
        variantTitle: variant?.attributes?.find(
          (attribute) => attribute?.name === 'title',
        )?.value,
        shortName: variant?.attributes?.find(
          (attribute) => attribute?.name === 'short-name',
        )?.value,
      })),
    };
  });

  const selectedMainVariant = findVariantBySku({
    variants: product.variants,
    sku: selectedSKU,
  });

  const selectedVariantSizeAttribute = findVariantAttributeByName(
    selectedMainVariant,
    SIZE_ATTRIBUTE_NAME,
  );

  const bundleGiftAovItems = useMemo(
    () =>
      bundleGiftsAov?.options?.map((item) => ({
        ...item,
        gift: {
          ...item.gift,
          quantity:
            selectedVariantSizeAttribute?.key?.includes('twin') &&
            item.gift.productKey === PURPLE_CLOUD_PILLOW
              ? 1
              : item.gift.quantity,
        },
      })),
    [bundleGiftsAov, selectedVariantSizeAttribute?.key],
  );

  const aovTitle = (
    giftsAov?.title?.[locale]
    || buyMoreAov?.title?.[locale]
    || flatRateAov?.title?.[locale]
    || bundleGiftsAov?.title?.[locale]
    || ''
  );

  return (
    <FlyoutModal
      isVisible
      onCloseModal={onCloseModal}
      title={aovTitle}
      backButtonLabel={formatMessage(messages.backLabel)}
      subHeader={null}
      theme={FLYOUT_MODAL_THEME_AOV}
      showCloseButton={!flatRateAov && !giftsAov && !bundleGiftsAov}
    >
      <ProductSelectedHeader>
        <ul>
          {formattedItems.map(({ id, name, shortName }) => (
            <li key={id}>
              {shortName || name[locale]} {formatMessage(messages.addedToCart)}
            </li>
          ))}
        </ul>
      </ProductSelectedHeader>

      {!!promotionalBanner && !flatRateAov && !giftsAov && !bundleGiftsAov && (
        <PromoBannerTracker addedSkus isModal progressBarEnabled />
      )}

      {buyMoreAov && (
        <BuyMoreSaveMore
          title={buyMoreAov.title}
          description={buyMoreAov.description}
          discountsTier={buyMoreAov.discountsTier}
          items={buyMoreAov.items}
        />
      )}
      {giftsAov && (
        <FreeGiftAOV
          title={giftsAov?.title}
          description={giftsAov?.description}
          summaryDisclaimer={giftsAov?.summaryDisclaimer}
          onCloseModal={onCloseModal}
          promotionalBanner={promotionalBanner?.tiers && promoBanner}
        >
          {!!promotionalBanner && (
            <PromoBannerTracker hasEstimatedValue isModal progressBarEnabled />
          )}
        </FreeGiftAOV>
      )}
      {flatRateAov && (
        <FlatPercentOff
          title={flatRateAov.title}
          description={flatRateAov.description}
          summaryDisclaimer={flatRateAov.summaryDisclaimer}
          items={flatRateAov.items}
          onCloseModal={onCloseModal}
          formattedItems={formattedItems}
          hasPromotionalBanner={!!promotionalBanner}
          mainProductAttributes={selectedMainVariant?.attributes}
        >
          {!!promotionalBanner && (
            <PromoBannerTracker hasEstimatedValue isModal progressBarEnabled />
          )}
        </FlatPercentOff>
      )}
      {bundleGiftsAov && (
        <BundleGiftAOV
          title={bundleGiftsAov.title}
          description={bundleGiftsAov.description}
          bundleName={bundleGiftsAov.bundleName}
          image={bundleGiftsAov.image}
          items={bundleGiftAovItems}
          onCloseModal={onCloseModal}
        >
          {!!promotionalBanner && (
            <PromoBannerTracker hasEstimatedValue isModal progressBarEnabled />
          )}
        </BundleGiftAOV>
      )}
    </FlyoutModal>
  );
};

AovModal.propTypes = {
  onCloseModal: PropTypes.func.isRequired,
  aovs: PropTypes.arrayOf(PropTypes.object.isRequired).isRequired,
};

export default AovModal;
